'use client'

import { useEffect, useState, useContext } from 'react'
import useFormContext from '@/context/useFormContext'
import { useForm } from 'react-hook-form'
import Input from '@/components/Input.tsx'
import { Select } from '@/components/Select.tsx'
import { CtaCard } from '@/components/CardButton.tsx'
import { Heading } from '@/components/Heading.tsx'
import { Box } from '@/components/Box.tsx'
import { selectTitleOptions } from '@/data/selectOptions.tsx'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@/components/Button.tsx'
import Textarea from '@/components/Textarea.tsx'
import { Header } from '@/components/HeaderPersonal'
import { subYears, format } from 'date-fns'
import { emailRegex, mobileNumberRegex } from '@/utils/constant'
import { Tooltip } from 'react-tooltip'
import { ReactSVG } from 'react-svg'
import { DealerContext } from '@/context/DealerContext'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { useFormValidation } from '@/context/FormValidationContext'
import { useFormSubmission } from '@/hooks/useFormSubmission'
import { ROUTES } from '@/config/routes'
import { preparePayload } from '@/services/saveService'
import { useGTMTracking } from '@/hooks/useGTMTracking'

// Also a person can't be older than 100 years, so today minus 100 years
// A person need to be at least 18 years old, so today minus 18 years
const minDateOfBirth = format(subYears(new Date(), 100), 'yyyy-MM-dd')
const maxDateOfBirth = format(subYears(new Date(), 18), 'yyyy-MM-dd')

interface RequestCallbackModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  defaultValues?: {
    firstName?: string
    lastName?: string
    mobileNumber?: string
    email?: string
  }
  trackPersonalCallbackRequested: () => void
}

function RequestCallbackModal({ open, setOpen, defaultValues, trackPersonalCallbackRequested }: RequestCallbackModalProps) {
  const { state } = useFormContext()
  const dealerContext = useContext(DealerContext)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const { register, handleSubmit, formState: { errors }, setValue, trigger } = useForm({
    defaultValues: {
      firstName: defaultValues?.firstName || '',
      lastName: defaultValues?.lastName || '',
      mobileNumber: defaultValues?.mobileNumber || '',
      email: defaultValues?.email || '',
      preferredCallTime: '',
      comments: ''
    }
  })

  // Update form values when defaultValues change
  useEffect(() => {
    if (open) {
      setValue('firstName', defaultValues?.firstName || '')
      setValue('lastName', defaultValues?.lastName || '')
      setValue('mobileNumber', defaultValues?.mobileNumber || '')
      setValue('email', defaultValues?.email || '')
    }
  }, [open, defaultValues, setValue])

  // Register all custom select components https://react-hook-form.com/faqs#Whatifyoudonthaveaccesstoref
  useEffect(() => {
    register('preferredCallTime', { required: 'Preferred call time is required' })
  })

  const { handleCallbackRequest } = useFormSubmission({
    onSuccess: () => {
      setIsSuccess(true)
      setIsSubmitting(false)
      setOpen(false)
    },
    onError: (error) => {
      console.error('Error during form save:', error)
      window.alert('We are sorry, but we were unable to submit your request. Please try again later.')
      setIsSubmitting(false)
    }
  })

  const onSubmit = async (data: {
    firstName: string
    lastName: string
    mobileNumber: string
    email: string
    preferredCallTime: string
    comments: string
  }) => {
    setIsSubmitting(true)
    
    // Track callback request event
    trackPersonalCallbackRequested()
    
    handleCallbackRequest({
      formId: state.formId,
      ...data,
      phone: data.mobileNumber,
      isPersonal: true,
      dealerId: dealerContext?.dealerInformation.dealerId?.toString() || ''
    })
  }

  const handleReturnToHome = () => {
    setIsSuccess(false)
    window.location.href = 'https://www.udc.co.nz/' // TODO @Adam : please check this and update as required, copied from the original code but was wondering if it should be coming from themes settings default home link of dealker site?
  }

  return (
    <>
    <Dialog open={open} onClose={() => setOpen(false)} className='relative z-20'>
      <DialogBackdrop
        transition
        className='fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in'
      />
      <div className='fixed inset-0 z-10 w-screen overflow-y-auto'>
        <div className='flex min-h-full items-end justify-center py-4 text-center sm:items-center sm:p-0'>
          <DialogPanel
            transition
            className='relative transform rounded-lg bg-white pt-5 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-xl sm:py-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95'
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='flex items-center justify-between px-4 sm:px-6 pb-5 border-b border-[var(--form-box-divider)]'>
                <DialogTitle as='h3' className='text-2xl'>
                  Request a call back
                </DialogTitle>
                <button
                  className='cursor-pointer transition duration-300 flex items-center justify-center rounded-full p-2 hover:bg-[var(--theme-bg)] hover:text-[var(--brand-1)] relative -right-4'
                  type='button'
                  onClick={() => setOpen(false)}
                >
                  <XMarkIcon className='size-6' aria-hidden='true' />
                </button>
              </div>
              <div className='p-4 sm:p-6'>
                <p className='text-base'>
                  Please enter your contact information, select a preferred call back time, and add any relevant comments if needed. Once you're ready, submit your request, and we'll get in touch with you as soon as possible.
                </p>
                <div className='space-y-7'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8 pt-10'>
                    <Input
                      label='First name'
                      inputId='firstName'
                      required={true}
                      placeholder='First name'
                      {...register('firstName', { required: 'First name is required' })}
                      error={errors.firstName}
                    />
                    <Input 
                      label='Last name'
                      inputId='lastName'
                      required={true}
                      placeholder='Last name'
                      {...register('lastName', { required: 'Last name is required' })}
                      error={errors.lastName}
                    />
                  </div>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                    <Input 
                      label='Mobile number' 
                      inputId='mobileNumber' 
                      required={true}
                      placeholder='Mobile number'
                      type='tel'
                      {...register('mobileNumber', { 
                        required: 'Mobile number is required',
                        pattern: {
                          value: mobileNumberRegex,
                          message: 'Invalid mobile number format'
                        }
                      })}
                      error={errors.mobileNumber}
                    />
                    <Input 
                      label='Email address'
                      inputId='email' 
                      required={true}
                      placeholder='Email address'
                      type='email'
                      {...register('email', { 
                        required: 'Email address is required',
                        pattern: {
                          value: emailRegex,
                          message: 'Invalid email address format'
                        }
                      })}
                      error={errors.email}
                    />
                  </div>
                  <div>
                    <Select
                      label='Preferred call time'
                      required={true}
                      selectOptions={[
                        { value: '', name: 'Select time slot' },
                        { value: 'Morning (9am - 12pm)', name: 'Morning (9am - 12pm)' },
                        { value: 'Afternoon (12pm - 3pm)', name: 'Afternoon (12pm - 3pm)' },
                        { value: 'Late afternoon (3pm - 5pm)', name: 'Late afternoon (3pm - 5pm)' },
                        { value: 'Anytime', name: 'Anytime' }
                      ]}
                      defaultValue={''}
                      onChange={(value) => {
                        setValue('preferredCallTime', value)
                        trigger('preferredCallTime')
                      }}
                      error={errors.preferredCallTime}
                    />
                  </div>
                  <div>
                    <Textarea
                      label='Comments'
                      inputId='comments'
                      required={true}
                      placeholder='Comments'
                      {...register('comments', { required: 'Comments are required' })}
                      error={errors.comments}
                    />
                  </div>
                </div>
              </div>
              <div className='flex justify-end gap-2 px-4 sm:px-6 pt-4'>
                <Button icon='none' style='text' onClick={() => setOpen(false)}>Cancel</Button>
                <Button type='submit' icon='none' disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Request a callback'}
                </Button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
    <Dialog open={isSuccess} onClose={() => setIsSuccess(false)} className='relative z-30'>
      <DialogBackdrop
        transition
        className='fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in'
      />
      <div className='fixed inset-0 z-10 w-screen overflow-y-auto'>
        <div className='flex min-h-full items-end justify-center py-4 text-center sm:items-center sm:p-0'>
          <DialogPanel
            transition
            className='relative transform rounded-lg bg-white pt-6 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-xs data-closed:sm:translate-y-0 data-closed:sm:scale-95'
          >
            <div>
              <div className='p-4 sm:p-6'>
                <div className='flex flex-col gap-4 items-center justify-center'>
                  <div className=''>
                    <CheckCircleIcon className='size-24 text-[var(--theme-success)]' aria-hidden='true' />
                  </div>
                    <DialogTitle as='h3' className='text-2xl text-center mt-1'>
                      Call back requested
                    </DialogTitle>
                  <p className='text-base text-center -mt-2.5'>
                    We will be in touch shortly.
                  </p>
                  <Button icon='none' className='w-full mt-4' onClick={handleReturnToHome}>
                    Return to home
                  </Button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
    </>
  )
}

const YourDetails = () => {
  const { state, dispatch } = useFormContext()
  const dealerContext = useContext(DealerContext)
  const { trackPersonalCallbackRequested } = useGTMTracking()
  const { register, formState: { errors, isSubmitted }, handleSubmit, setValue, trigger, watch, reset } = useForm({
    defaultValues: {
      title: state.personal.yourDetails.title,
      firstName: state.personal.yourDetails.firstName,
      middleName: state.personal.yourDetails.middleName,
      lastName: state.personal.yourDetails.lastName,
      mobileNumber: state.personal.yourDetails.mobileNumber,
      email: state.personal.yourDetails.email,
      dateOfBirth: state.personal.yourDetails.dateOfBirth
    }
  })
  
  // Add this effect to update form values when context changes (e.g., after API loads)
  useEffect(() => {
    reset({
      title: state.personal.yourDetails.title,
      firstName: state.personal.yourDetails.firstName,
      middleName: state.personal.yourDetails.middleName,
      lastName: state.personal.yourDetails.lastName,
      mobileNumber: state.personal.yourDetails.mobileNumber,
      email: state.personal.yourDetails.email,
      dateOfBirth: state.personal.yourDetails.dateOfBirth
    })
  }, [state.personal.yourDetails, reset])

  const { registerValidationTrigger, unregisterValidationTrigger } = useFormValidation()
  const triggerFormValidation = () => handleSubmit(() => {})()

  // Register the validation trigger when the component mounts
  useEffect(() => {
    registerValidationTrigger(triggerFormValidation)
    return () => unregisterValidationTrigger()
  }, [registerValidationTrigger, unregisterValidationTrigger])

  useEffect(() => {
    // We need to attach required dealer details to the form context
    if (dealerContext?.dealerInformation.dealerId || dealerContext?.dealerInformation.dealerUniqueIdentifier) {
      dispatch({
        type: 'UPDATE_DEALER_DETAILS',
        payload: {
          dealerId: dealerContext?.dealerInformation.dealerId ?? null,
          dealerUniqueIdentifier: dealerContext?.dealerInformation.dealerUniqueIdentifier ?? null
        }
      })
    }
  }, [dealerContext?.dealerInformation.dealerId, dealerContext?.dealerInformation.dealerUniqueIdentifier, dispatch])

  const [open, setOpen] = useState(false)
  const { handleFormSave } = useFormSubmission({
    onSuccess: (response) => {
      // We used to dispatch the data here, let's leave it as is for now as it could be useful
      console.log('Form submission successful:', response)
      // Update our context with the response.data.formId
      dispatch({
        type: 'UPDATE_FORM_METADATA',
        payload: {
          formId: response.data.formId,
          dealerId: response.data.dealerId
        }
      })
    },
    onError: (error) => {
      console.error('Error during form save:', error)
    }
  })

  const dispatchFormData = (data: any) => {
    console.log('Form dispatch:', data)
    dispatch({
      type: 'UPDATE_PERSONAL',
      step: 'yourDetails',
      payload: data.personal.yourDetails
    })
  }

  // Get current form values
  const currentFormValues = watch()

  // Register all custom select components https://react-hook-form.com/faqs#Whatifyoudonthaveaccesstoref
  useEffect(() => {
    register('title', { required: 'Title is required' })
  })

  // Handle our custom select component
  const handleSelectChange = (fieldName: string, value: string) => {
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    setValue(fieldName, value)
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    if (isSubmitted) trigger(fieldName)
  }

  const handleRequestCallbackClick = () => {
    setOpen(true)
  }

  const onSubmit = (data: any) => {
    data = {
      ...state,
      business: null,
      personal: {
        ...state.personal,
        yourDetails: {
          ...state.personal.yourDetails,
          title: data.title,
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          mobileNumber: data.mobileNumber,
          email: data.email,
          dateOfBirth: data.dateOfBirth
        }
      }
    }

    dispatchFormData(data)

    // Depending on the step, we want to send the payload to the API expecting to pass only the current step data
    // and all other steps as null, we first want to deepClone the state, then set other loan steps to null
    handleFormSave(preparePayload(data, 'personal', 'yourDetails'), ROUTES.PERSONAL.ABOUT_YOU.path)
  }

  return (
    <>
      <Header />
      <Tooltip className='Tooltip Tooltip--info' id='tooltip-holder' />
      <Heading>Your personal information</Heading>
      {state.isPostCall && (
        <p className="text-center text-gray-600 mb-6 max-w-2xl mx-auto">We've included some information from your most recent loan in the form below. You just need to review this information and update anything if necessary.</p>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box>
          <div className='grid grid-cols-1 md:grid-cols-3 md:gap-8'>
            <Select 
              label='Title'
              required={true}
              selectOptions={selectTitleOptions}
              defaultValue={state.personal.yourDetails.title}
              onChange={value => handleSelectChange('title', value)}
              error={errors.title}
            />
          </div>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
            <Input
              label='First name'
              inputId='firstName'
              required={true}
              placeholder='First name'
              {...register('firstName', { required: 'First name is required' })}
              error={errors.firstName}
            />
            <Input 
              label='Middle name' 
              inputId='middleName'
              placeholder='Middle name'
              {...register('middleName')}
            />
            <Input 
              label='Last name'
              inputId='lastName'
              required={true}
              placeholder='Last name'
              {...register('lastName', { required: 'Last name is required' })}
              error={errors.lastName}
            />
          </div>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
            <Input 
              label='Mobile number' 
              inputId='mobileNumber' 
              required={true}
              placeholder='Mobile number'
              {...register('mobileNumber', { 
                required: 'Mobile number is required',
                minLength: { value: 8, message: 'Mobile number must be at least 8 digits' },
                pattern: {
                  value: mobileNumberRegex,
                  message: 'Invalid mobile number format'
                }
              })}
              type='tel'
              error={errors.mobileNumber}
            />
            <Input 
              label='Email address'
              inputId='EmailAdress' 
              required={true}
              placeholder='Email address'
              {...register('email', { 
                required: 'Email address is required',
                pattern: {
                  value: emailRegex,
                  message: 'Invalid email address format'
                }
              })}
              type='email'
              error={errors.email}
            />
            <Input 
              label='Date of birth' 
              inputId='DateofBirth'
              required={true}
              {...register('dateOfBirth', { required: 'Date of birth is required' })}
              type='date'
              min={minDateOfBirth}
              max={maxDateOfBirth}
              error={errors.dateOfBirth}
            />
          </div>
        </Box>
        { dealerContext?.callBackEnabled ?
          <div className='max-w-6xl mx-auto justify-center flex flex-col md:flex-row max-md:items-center gap-8 py-8 '>
            <CtaCard title='callback' onClick={handleRequestCallbackClick} tooltip='Have questions? Request a call back at a time that is convenient to you.'>
              <ReactSVG className='Card-svg size-[108px] flex items-center justify-center' src={dealerContext?.dealerThemeIcon?.requestCallBack || '/icons/callback.svg'} />
            </CtaCard>
            <CtaCard title='continue' type='submit' tooltip='Ready to proceed? Click here to continue filling out the form online.'>
              <ReactSVG className='Card-svg size-[108px] flex items-center justify-center' src={dealerContext?.dealerThemeIcon?.continueApplication || '/icons/continue.svg'} />
            </CtaCard>
          </div>
          :
          <div className='flex justify-center mt-8 pb-16 max-w-5xl mx-auto'>
            <Button type='submit'> Next </Button>
          </div>
        }
        {state.isPostCall && (
          <p className="text-center text-gray-600 mb-6 max-w-2xl mx-auto">By clicking "Continue" you are confirming that the above information is correct</p>
        )}
      </form>
      <RequestCallbackModal 
        open={open} 
        setOpen={setOpen} 
        defaultValues={{
          firstName: currentFormValues.firstName,
          lastName: currentFormValues.lastName,
          mobileNumber: currentFormValues.mobileNumber,
          email: currentFormValues.email
        }}
        trackPersonalCallbackRequested={trackPersonalCallbackRequested}
      />
    </>
  )
}

export default YourDetails
